import { RepeatIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Select,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { ExportButton } from "../components/ui/exportButton";
import { secureFetch } from "../data/secureFetch";
import { useFileExport } from "../hooks/use-file-export";
import { MAX_EXPORT_SIZE } from "../utils/constants";
import { builQueryString, fiterFilters, objectToString } from "../utils/parser";
import UniversalTable from "./UniversalTable";
const {
  formatCentsToDollars,
  formatDateTimeToLocalString,
} = require("../store/calc-helpers");

const defaultPagination = {
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
  totalPages: 0,
};

const defaultSubscriptionFilter = {
  search: "",
  status: "",
  minAmount: "",
  maxAmount: "",
  donation_type: "",
  page: 1,
  itemsPerPage: 10,
};

function SubscriptionsFilters({
  filters,
  updateFilter,
  handleReset,
  handleExport,
  exportLoading = false,
  onRefresh,
}) {
  return (
    <Box mb={6}>
      <Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
        {/* SEARCH */}
        <Flex
          align="center"
          justify="space-between"
          p={4}
          borderRadius="lg"
          gap={6}
        >
          <InputGroup width={["100%", "260px"]}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>

            <Input
              variant="filled"
              placeholder="Search subscriptions..."
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>
          {onRefresh && (
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="blue"
              variant="solid"
              onClick={onRefresh}
              px={4}
              _hover={{ transform: "scale(1.05)" }}
              transition="all 0.2s"
            >
              refresh
            </Button>
          )}
        </Flex>
        {/* RIGHT SECTION */}
        <Flex align="center" gap={3}>
          {/* FILTER POPUP */}
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button colorScheme="gray" variant="outline">
                Filters
              </Button>
            </PopoverTrigger>

            <PopoverContent p={4} borderRadius="lg" shadow="xl" width="300px">
              <PopoverArrow />
              <PopoverCloseButton />

              <PopoverBody pt={4}>
                {/* Frequency */}
                <Select
                  placeholder="Frequency"
                  value={filters.donation_type}
                  onChange={(e) =>
                    updateFilter("donation_type", e.target.value)
                  }
                  mb={4}
                >
                  <option value="monthly">Monthly</option>
                  <option value="one_time">One time</option>
                </Select>
                <Select
                  placeholder="Status"
                  value={filters.status}
                  onChange={(e) => updateFilter("status", e.target.value)}
                  mb={4}
                >
                  {/* Subscriptions: 'active', 'canceled' */}
                  <option value="active">Active</option>
                  <option value="canceled">Canceled</option>
                </Select>

                {/* AMOUNT RAISED RANGE */}
                <FormLabel fontSize="sm" mb={2} fontWeight="medium">
                  Amount Range
                </FormLabel>
                <Flex gap={3} mb={4}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Min"
                      value={filters.minAmount}
                      onChange={(e) =>
                        updateFilter("minAmount", e.target.value)
                      }
                    />
                  </InputGroup>

                  <InputGroup>
                    <InputLeftElement pointerEvents="none">$</InputLeftElement>
                    <Input
                      variant="filled"
                      type="number"
                      placeholder="Max"
                      value={filters.maxAmount}
                      onChange={(e) =>
                        updateFilter("maxAmount", e.target.value)
                      }
                    />
                  </InputGroup>
                </Flex>

                {/* RESET */}
                <Button
                  width="100%"
                  mt={2}
                  colorScheme="red"
                  variant="outline"
                  onClick={handleReset}
                >
                  Reset Filters
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* EXPORT DROPDOWN */}
          <ExportButton
            exportLoading={exportLoading}
            handleExport={handleExport}
          />
        </Flex>
      </Flex>
    </Box>
  );
}

const ListSubscriptions = () => {
  const { exportFile } = useFileExport();
  const toast = useToast();
  const { organizationId } = useSelector((state) => state.user);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pagination, setPagination] = useState(defaultPagination);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState(defaultSubscriptionFilter);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortRules, setSortRules] = useState([]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchSubscriptions = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const queryString = builQueryString(filters);
      const response = await secureFetch(
        `/api/subscriptions/${organizationId}${queryString}`
      );
      if (response.ok) {
        // Response structure: { data: [...], pagination: {...} }
        const subscriptionsData = response.data || [];
        const formattedData = subscriptionsData.map((subscription) => ({
          ...subscription,
          amount: `$${formatCentsToDollars(subscription.donation_amount)}`,
          next_donation_date: formatDateTimeToLocalString(
            subscription.next_donation_date
          ),
          name: `${subscription.first_name} ${subscription.last_name}`,
        }));

        setPagination(response.pagination);
        setSubscriptions(formattedData);
      } else {
        throw new Error("Failed to fetch subscriptions");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters]);

  const handleExport = async (format = "csv") => {
    try {
      setExportLoading(true);

      // Build export filters without pagination limits
      const exportFilters = {
        ...filters,
        page: 1,
        itemsPerPage: MAX_EXPORT_SIZE, // Maximum for export
      };
      const queryString = builQueryString(exportFilters);
      const response = await secureFetch(
        `/api/subscriptions/${organizationId}${queryString}`
      );
      if (response.ok) {
        // Response structure: { data: [...], pagination: {...} }
        const subscriptionsData = response.data || [];
        const formattedData = subscriptionsData.map((subscription) => ({
          ...subscription,
          amount: formatCentsToDollars(subscription.donation_amount),
          next_donation_date: formatDateTimeToLocalString(
            subscription.next_donation_date
          ),
          name: `${subscription.first_name} ${subscription.last_name}`,
        }));

        exportFile({
          format,
          data: formattedData,
          columns,
          exportFileName: "Subscriptions Records",
          tableName: "Subscriptions Records",
          filtersValues: { organizationId },
          sortRules,
        });
      } else {
        throw new Error("Failed to fetch export data");
      }
    } catch (err) {
      setError();
      toast({
        position: "top-right",
        title: `Export failed: ${err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const columns = [
    {
      Header: "ID",
      accessor: "id",
    },
    {
      Header: "Amount",
      accessor: "amount",
    },
    {
      Header: "Email",
      accessor: "email",
    },
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Next Donation Date",
      accessor: "next_donation_date",
    },
    {
      Header: "Frequency",
      accessor: "donation_type",
    },
    {
      Header: "Status",
      accessor: "status",
    },
  ];

  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  return (
    <Box p={5}>
      <Helmet>
        <title>Subscriptions - PurelyGive</title>
        <meta
          name="description"
          content="Set up and manage recurring donations with PurelyGive's subscription features. Ensure ongoing support for your cause."
        />
        <meta
          name="keywords"
          content="subscriptions, recurring donations, ongoing support, fundraising, PurelyGive"
        />
        <meta
          property="og:title"
          content="Manage Recurring Donations - PurelyGive"
        />
        <meta
          property="og:description"
          content="Enable and oversee recurring donations to your campaigns using PurelyGive's subscription management tools."
        />
        <meta property="og:image" content={campaignImage} />
        <meta
          property="og:url"
          content="https://purelygive.com/subscriptions"
        />
      </Helmet>

      <Heading mb={6}>Subscriptions</Heading>

      <SubscriptionsFilters
        filters={filters}
        updateFilter={updateFilter}
        handleExport={handleExport}
        handleReset={() => {
          setFilters(defaultSubscriptionFilter);
        }}
        onRefresh={() => {
          setFilters(defaultSubscriptionFilter);
          fetchSubscriptions();
        }}
        exportLoading={exportLoading}
      />
      <UniversalTable
        isLoading={loading}
        error={error}
        showGlobalSearch={false}
        showColumnFilters={false}
        columns={columns}
        data={subscriptions}
        tableName="Subscriptions"
        pagination={pagination}
        onRetry={fetchSubscriptions}
        showPagination
        onPaginationChange={(page, itemsPerPage) => {
          updateFilter("itemsPerPage", itemsPerPage);
          updateFilter("page", page);
        }}
        isFiltered={
          objectToString(fiterFilters(filters, ["page", "itemsPerPage"])) !== ""
        }
        enableMultiSort={true}
        initialSortBy={sortRules}
        onSortChange={(rules) => {
          setSortRules(rules);
        }}
      />
    </Box>
  );
};

export default ListSubscriptions;
